// composables/usePassengerCounter.js
import { computed, ref } from 'vue';

export function usePassengerCounter (passengers, passengerType, config = {}) {
	const {
		minCount = passengerType === 'adults' ? 1 : 0,
		maxCount = 9,
		maxTotal = 9,
		includeAges = passengerType !== 'adults'
	} = config;

	const activeAgeIndex = ref(
		includeAges
			? passengers.value[passengerType]?.ages?.findIndex(age => age === null) ?? -1
			: -1
	);

	// Calculate current total passengers
	const totalPassengers = computed(() => {
		if (passengerType === 'adults' || passengerType === 'child') {
			return passengers.value.adults + passengers.value.child.count;
		}
		return passengers.value[passengerType].count;
	});

	// Check if increment is disabled
	const isIncrementDisabled = computed(() => {
		const count = passengerType === 'adults'
			? passengers.value.adults
			: passengers.value[passengerType].count;

		return count >= maxCount || totalPassengers.value >= maxTotal;
	});

	// Check if decrement is disabled
	const isDecrementDisabled = computed(() => {
		const count = passengerType === 'adults'
			? passengers.value.adults
			: passengers.value[passengerType].count;

		return count <= minCount;
	});

	// Increment passenger count
	function increment () {
		if (isIncrementDisabled.value) {
			return;
		}

		if (passengerType === 'adults') {
			passengers.value.adults += 1;
		} else {
			passengers.value[passengerType].ages.push(null);
			passengers.value[passengerType].count += 1;
			setNextActiveAgeIndex();
		}
	}

	// Decrement passenger count
	function decrement () {
		if (isDecrementDisabled.value) {
			return;
		}

		if (passengerType === 'adults') {
			passengers.value.adults -= 1;
		} else {
			if (passengers.value[passengerType].count > 0) {
				passengers.value[passengerType].ages.pop();
				passengers.value[passengerType].count -= 1;
			}
			activeAgeIndex.value = passengers.value[passengerType]?.ages?.findIndex(age => age === null) ?? -1;
		}
	}

	// Set the next active age index (for age selection)
	function setNextActiveAgeIndex () {
		activeAgeIndex.value = passengers.value[passengerType]?.ages?.findIndex(age => age === null) ?? -1;

		// Scroll into view the age selector for the newly added passenger
		const ageElements = document.querySelectorAll('.abPassengers__age');
		if (activeAgeIndex.value >= 0 && activeAgeIndex.value < ageElements.length) {
			const el = ageElements[activeAgeIndex.value];
			const container = el.parentElement;

			container.scrollTo({
				left: el.offsetLeft - container.clientWidth / 2 + el.clientWidth / 2,
				behavior: 'smooth'
			});
		}
	}

	// Set age for a specific passenger
	function setAge (age) {
		passengers.value[passengerType].ages[activeAgeIndex.value] = age;
		setNextActiveAgeIndex();
	}

	function removeAge (index) {
		passengers.value[passengerType].ages.splice(index, 1);
		passengers.value[passengerType].count -= 1;
		setNextActiveAgeIndex();
	}

	return {
		activeAgeIndex,
		isIncrementDisabled,
		isDecrementDisabled,
		increment,
		decrement,
		setAge,
		removeAge,
		setActiveAgeIndex: (index) => {
			activeAgeIndex.value = index;
		}
	};
}
